import { Alert, Pressable, Text, View } from "react-native";
import { Image } from "expo-image";
import * as ImagePicker from "expo-image-picker";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";

type AvatarPickerProps = {
  avatarUri: string | null;
  onChangeAvatar: (uri: string | null) => void;
  /**
   * Initials or fallback character to show when there is no avatar.
   * Example: "C" for Chappell.
   */
  initials?: string;
  /**
   * Avatar size in pixels. Defaults to 112 (same as edit profile screen).
   */
  size?: number;
};

export function AvatarPicker({
  avatarUri,
  onChangeAvatar,
  initials = "?",
  size = 112,
}: AvatarPickerProps) {
  const [primaryForeground, brand] = useCSSVariable([
    "--color-primary-foreground",
    "--color-brand",
  ]) as [string, string];

  const pickAvatar = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== "granted") {
      Alert.alert(
        "Permissão necessária",
        "Permita o acesso à sua biblioteca de fotos para selecionar uma foto de perfil."
      );
      return;
    }

    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ["images"],
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets?.[0]) {
        onChangeAvatar(result.assets[0].uri);
      }
    } catch {
      Alert.alert("Erro", "Não foi possível selecionar a imagem.");
    }
  };

  const handleRemove = () => {
    onChangeAvatar(null);
  };

  return (
    <View className="items-center py-6">
      <Pressable
        onPress={pickAvatar}
        className="relative"
        accessibilityLabel="Selecionar foto de perfil"
        accessibilityRole="button"
      >
        {avatarUri ? (
          <Image
            source={{ uri: avatarUri }}
            className="rounded-full"
            contentFit="cover"
            style={{ width: size, height: size, borderRadius: size / 2 }}
          />
        ) : (
          <View
            className="bg-brand items-center justify-center rounded-full"
            style={{ width: size, height: size }}
          >
            <Text className="text-brand-foreground text-3xl font-bold">
              {initials}
            </Text>
          </View>
        )}
        <View className="bg-primary border-background absolute right-0 bottom-0 size-9 items-center justify-center rounded-full border-2">
          <Ionicons name="pencil" size={16} color={primaryForeground} />
        </View>
      </Pressable>
      {avatarUri && (
        <Pressable onPress={handleRemove} className="mt-3">
          <Text className="text-sm" style={{ color: brand }}>
            Remover foto
          </Text>
        </Pressable>
      )}
    </View>
  );
}
